import {
  IconArrowLeft,
  IconCirclePlus,
  IconFilter,
  IconTrash,
} from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import {
  BasicDatePicker,
  BasicTable,
  Loading,
  ScalrEmptyBox,
  SingleBranchSelect,
  TreeSelector,
  TreeSelectorParent,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { ScalrTableColumn } from '@/components/customComponents'
import { toast, useKpiBranchGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { KpiBranchAssignmentCreationModel } from '@/types'
import { formatValueForKpi } from '@/utils'
import { DateType } from '@/utils/date'

interface Selectable {
  id: string
  name: string
}

type AdditionSelections = {
  branch?: string // Branch name from SingleBranchSelect
  dateRange?: DateRange
  dateFilterType?: DateType
  kpi?: Selectable
}

interface CreateGoalPlanProps {
  onCancel: () => void
  onSave: () => void
}

interface KpiBranchAssignmentData {
  id: number
  branchName: string
  kpiMetric: string
  year: number
  quarter: number
  targetValue: number
  // Display fields
  kpiName: string
  kpiUnit: string
}

const getKpiAddFormColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'branch',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'kpiName',
    header: t('targets.table.kpiName'),
    type: 'text',
  },
  {
    accessorKey: 'date',
    header: t('targets.table.date'),
    type: 'text',
  },
  {
    accessorKey: 'target',
    header: t('targets.table.target'),
    type: 'text',
  },
]

export const CreateBranchGoalPlan = ({
  onCancel,
  onSave,
}: CreateGoalPlanProps) => {
  const { t } = useTranslation()
  const [additionSelections, setAdditionSelections] =
    useState<AdditionSelections>({
      branch: undefined,
      dateRange: undefined,
      dateFilterType: 'quarterly',
      kpi: undefined,
    })
  const [targetValue, setTargetValue] = useState(0)
  const [kpiBranchAssignments, setKpiBranchAssignments] = useState<
    KpiBranchAssignmentData[]
  >([])
  const [selectedKpiId, setSelectedKpiId] = useState<string>()

  const { fetchMetadata, createBranchAssignments, metadata, loading, error } =
    useKpiBranchGoalAssignments()

  // Show error toast if there is an error
  useEffect(() => {
    if (error) {
      toast({
        title: t('common.errors.serverError'),
        description: error.message,
        variant: 'destructive',
      })
    }
  }, [error])

  // Transform metadata.availableKpis to TreeSelector format
  const kpiTreeData: TreeSelectorParent[] = useMemo(() => {
    if (!metadata?.availableKpis) return []

    return Object.entries(metadata.availableKpis).map(([category, kpis]) => ({
      id: category,
      label: category,
      children: kpis.map((kpi) => ({
        id: kpi.metricField,
        label: `${kpi.name}${kpi.unit ? ` (${kpi.unit})` : ''}`,
        disabled: false,
      })),
    }))
  }, [metadata])

  // Validate if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      !!additionSelections.branch &&
      !!selectedKpiId &&
      !!additionSelections.dateRange?.from &&
      targetValue > 0
    )
  }, [
    additionSelections.branch,
    additionSelections.dateRange,
    selectedKpiId,
    targetValue,
  ])

  const handleAddKPI = useCallback(() => {
    const selectedBranchName = additionSelections.branch
    const dateRange = additionSelections.dateRange

    if (
      !selectedBranchName ||
      !selectedKpiId ||
      !dateRange?.from ||
      !metadata ||
      targetValue <= 0
    ) {
      return
    }

    // Find KPI from metadata
    let selectedKpi:
      | { metricField: string; name: string; unit: string }
      | undefined
    for (const kpis of Object.values(metadata?.availableKpis || {})) {
      const kpi = kpis.find((k) => k.metricField === selectedKpiId)
      if (kpi) {
        selectedKpi = {
          metricField: kpi.metricField,
          name: kpi.name,
          unit: kpi.unit || '',
        }
        break
      }
    }
    if (!selectedKpi) {
      return
    }

    // Extract year and quarter from dateRange
    const year = dateRange.from.getFullYear()
    const quarter = Math.floor(dateRange.from.getMonth() / 3) + 1

    const newAssignment: KpiBranchAssignmentData = {
      id: kpiBranchAssignments.length + 1,
      branchName: selectedBranchName,
      kpiMetric: selectedKpi.metricField,
      year,
      quarter,
      targetValue,
      // Display fields
      kpiName: selectedKpi.name,
      kpiUnit: selectedKpi.unit,
    }
    setKpiBranchAssignments((prev) => [...prev, newAssignment])

    // Reset form fields after adding
    setSelectedKpiId(undefined)
    setTargetValue(0)
    setAdditionSelections((prev) => ({
      ...prev,
      dateRange: undefined,
    }))
  }, [
    kpiBranchAssignments,
    additionSelections.branch,
    additionSelections.dateRange,
    selectedKpiId,
    targetValue,
    metadata,
  ])

  const handleRemoveKPI = useCallback((id: number | string) => {
    setKpiBranchAssignments((prev) =>
      prev.filter((assignment) => assignment.id !== id)
    )
  }, [])

  const resetAdditionForm = useCallback(() => {
    setKpiBranchAssignments([])
    setAdditionSelections({
      branch: undefined,
      dateRange: undefined,
      dateFilterType: 'quarterly',
      kpi: undefined,
    })
    setTargetValue(0)
    setSelectedKpiId(undefined)
  }, [])

  const handleCancel = useCallback(() => {
    const hasChanges =
      additionSelections.branch ||
      selectedKpiId ||
      additionSelections.dateRange ||
      kpiBranchAssignments.length > 0 ||
      targetValue > 0

    if (hasChanges) {
      const confirmClose = window.confirm(t('targets.form.closeWithoutSaving'))
      if (confirmClose) {
        resetAdditionForm()
        onCancel()
      }
    } else {
      resetAdditionForm()
      onCancel()
    }
  }, [
    additionSelections.branch,
    additionSelections.dateRange,
    selectedKpiId,
    kpiBranchAssignments,
    targetValue,
    resetAdditionForm,
    onCancel,
    t,
  ])

  const handleSave = useCallback(async () => {
    if (kpiBranchAssignments.length === 0) {
      alert(t('targets.form.addAtLeastOneKpi'))
      return
    }

    // Map kpiAssignments to the API format (KpiAssignmentCreationModel)
    const assignmentsToSave: KpiBranchAssignmentCreationModel[] =
      kpiBranchAssignments.map((assignment) => ({
        branchName: assignment.branchName,
        kpiMetric: assignment.kpiMetric,
        year: assignment.year,
        quarter: assignment.quarter,
        targetValue: assignment.targetValue,
      }))

    try {
      await createBranchAssignments(assignmentsToSave)
      console.log('Successfully saved KPI assignments')
      resetAdditionForm()
      onSave()
    } catch (error) {
      console.error('Error saving KPI assignments:', error)
      toast({
        title: t('targets.errors.errorCreatingBranchAssignment'),
        description:
          error instanceof Error
            ? error.message
            : t('common.errors.serverError'),
        variant: 'destructive',
      })
    }
  }, [kpiBranchAssignments, createBranchAssignments, resetAdditionForm, onSave])

  const handleBranchSelect = useCallback(
    (branchName: string) => {
      setAdditionSelections((prev) => ({ ...prev, branch: branchName }))
      // Fetch metadata for the selected branch
      fetchMetadata(branchName)
    },
    [fetchMetadata]
  )

  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setAdditionSelections((prev) => ({
        ...prev,
        dateRange: newDate,
        dateFilterType: newDateFilterType,
      }))
    },
    []
  )

  const handleKpiTreeSelection = useCallback(
    (kpiId: string, categoryId: string) => {
      setSelectedKpiId(kpiId)
      console.log('Selected KPI:', kpiId, 'from category:', categoryId)
    },
    []
  )

  return (
    <div className="my-6">
      <div className="flex items-center gap-4 mb-4">
        <Button onClick={handleCancel}>
          <IconArrowLeft className="mr-2" />
          {t('common.buttons.back')}
        </Button>
        <h3 className="leading-none tracking-tight text-black-500 text-display-s">
          {t('targets.form.createBranchGoalPlan')}
        </h3>
      </div>

      <div>
        {/* Form Section */}
        <Card>
          <CardContent className="p-4">
            {!metadata && loading ? (
              <Loading />
            ) : (
              <div className="flex flex-col md:flex-row gap-6">
                {/* Filter Controls - Fixed 300px */}
                <div className="w-full md:w-[300px] md:flex-shrink-0 space-y-4">
                  <div className="flex flex-col gap-2">
                    <Label>{t('branchSelect.selectBranch')}</Label>
                    <SingleBranchSelect
                      selectedBranch={additionSelections.branch}
                      onSelectBranch={handleBranchSelect}
                    />
                  </div>

                  {/* Show KPI selector only when metadata is loaded */}
                  {metadata && (
                    <>
                      <div className="flex flex-col gap-2">
                        <Label>{t('targets.form.kpi')}</Label>
                        <TreeSelector
                          data={kpiTreeData}
                          selectedId={selectedKpiId}
                          onSelect={handleKpiTreeSelection}
                          placeholder={t('targets.form.selectKpi')}
                          loading={loading}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label>{t('targets.table.date')}</Label>
                        <BasicDatePicker
                          value={additionSelections.dateRange}
                          dateFilterType={additionSelections.dateFilterType}
                          onChange={handleDateChange}
                          noMonths
                          yearsUnselectable
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="targetValue">
                          {t('targets.form.targetValue')}
                        </Label>
                        <Input
                          id="targetValue"
                          type="number"
                          min="0"
                          step="any"
                          value={targetValue === 0 ? '' : targetValue}
                          onChange={(e) =>
                            setTargetValue(
                              e.target.value === '' ? 0 : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          className="w-[200px]"
                        />
                      </div>

                      {/* Action Button */}
                      <div className="flex gap-4 pt-2">
                        <Button
                          onClick={handleAddKPI}
                          size="sm"
                          disabled={!isFormValid || loading}
                          className="w-[200px]"
                        >
                          <IconCirclePlus className="mr-2" />
                          {t('targets.form.addBranchKpiGoal')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Table/EmptyBox Section - Takes remaining space */}
                <div className="flex-1 min-w-0">
                  {!additionSelections.branch ? (
                    <ScalrEmptyBox
                      icon={IconFilter as any}
                      title={t('targets.emptyStates.selectBranchTitle')}
                      message={t('targets.emptyStates.selectBranchMessage')}
                      size="md"
                    />
                  ) : (
                    <BasicTable
                      data={kpiBranchAssignments.map((assignment) => {
                        return {
                          id: assignment.id,
                          branch: assignment.branchName,
                          kpiName: assignment.kpiName,
                          date: `${assignment.year} Q${assignment.quarter}`,
                          target: formatValueForKpi(
                            assignment.targetValue,
                            assignment.kpiUnit
                          ),
                          action: (
                            <Button
                              variant="ghost"
                              onClick={() => handleRemoveKPI(assignment.id)}
                              size="sm"
                              className="h-8 w-8 p-0 bg-white border-none"
                            >
                              <IconTrash />
                            </Button>
                          ) as any,
                        }
                      })}
                      columns={[
                        ...getKpiAddFormColumns(t),
                        {
                          accessorKey: 'action',
                          header: '',
                          type: 'text',
                        },
                      ]}
                      title=""
                      loading={false}
                      enablePagination
                      itemsPerPage={6}
                      height={400}
                    />
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={handleCancel}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            disabled={kpiBranchAssignments.length === 0}
            type="submit"
            onClick={handleSave}
          >
            {t('common.buttons.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
